/* global React, ReactDOM, window */
const { useState, useEffect, useRef } = React;

const LS_KEY = "scolist-proto-v1";

// Sessão encerrada por inatividade (ver efeito no App). O login do Firebase é
// compartilhado entre as abas do navegador — signOut numa desloga todas —,
// então o "sem uso" é do navegador inteiro: cada aba grava a última
// interação em ACTIVITY_KEY. Aba parada há esse tempo com outra aba ativa
// não desloga: recarrega a lista do servidor antes de deixar editar.
const IDLE_LOGOUT_MS = 15 * 60 * 1000;
const ACTIVITY_KEY = "scolist:lastActivity";

// Vigia a inatividade enquanto `sessionKey` existir (conta logada). Chama
// `onExpire` quando o navegador inteiro está parado há IDLE_LOGOUT_MS e
// `onStale` quando só esta aba está (outra aba seguiu sendo usada). A
// interação que "acorda" a aba é engolida (listener em capture, antes do
// React) e as seguintes também, até o callback terminar — nenhuma edição sai
// em cima da lista velha. Callbacks via ref: o efeito só reinicia quando a
// sessão muda.
function useIdleSession(sessionKey, { onExpire, onStale }) {
  const callbacksRef = useRef({ onExpire, onStale });
  callbacksRef.current = { onExpire, onStale };
  useEffect(() => {
    if (!sessionKey) return undefined;
    const readShared = () => { try { return Number(localStorage.getItem(ACTIVITY_KEY)) || 0; } catch (e) { return 0; } };
    const writeShared = (t) => { try { localStorage.setItem(ACTIVITY_KEY, String(t)); } catch (e) {} };
    let tabLast = Date.now();
    let lastWrite = tabLast;
    let busy = false;
    writeShared(tabLast);

    // true = a aba estava parada; a interação não deve chegar no app.
    const check = () => {
      if (busy) return true;
      const now = Date.now();
      if (now - Math.max(tabLast, readShared()) > IDLE_LOGOUT_MS) {
        // Fica busy até o efeito desmontar (logout derruba a sessão).
        busy = true;
        Promise.resolve(callbacksRef.current.onExpire()).catch((e) => console.warn("Falha ao encerrar sessão por inatividade:", e));
        return true;
      }
      if (now - tabLast > IDLE_LOGOUT_MS) {
        busy = true;
        Promise.resolve(callbacksRef.current.onStale())
          .catch((e) => console.warn("Falha ao atualizar aba parada:", e))
          .finally(() => { tabLast = Date.now(); lastWrite = tabLast; writeShared(tabLast); busy = false; });
        return true;
      }
      return false;
    };
    const touch = () => {
      tabLast = Date.now();
      if (tabLast - lastWrite > 5000) { lastWrite = tabLast; writeShared(tabLast); }
    };
    const onInteraction = (e) => {
      if (check()) { e.stopPropagation(); if (e.cancelable) e.preventDefault(); return; }
      touch();
    };
    const onPassive = () => { if (!check()) touch(); };
    const onVisible = () => { if (document.visibilityState === "visible") check(); };
    const blocking = ["pointerdown", "mousedown", "click", "keydown"];
    const passive = ["wheel", "touchstart", "scroll"];
    blocking.forEach((t) => window.addEventListener(t, onInteraction, true));
    passive.forEach((t) => window.addEventListener(t, onPassive, { capture: true, passive: true }));
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    // Aba visível e parada também expira sozinha (não espera o clique).
    const timer = setInterval(() => { if (document.visibilityState === "visible") check(); }, 30 * 1000);
    return () => {
      blocking.forEach((t) => window.removeEventListener(t, onInteraction, true));
      passive.forEach((t) => window.removeEventListener(t, onPassive, { capture: true }));
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
      clearInterval(timer);
    };
  }, [sessionKey]);
}

// O progresso do wizard (data/current) era guardado num único slot global
// de localStorage, sem relação nenhuma com qual conta estava logada. A
// maior parte do wizard nunca teve persistência própria no backend — só
// livro pinado (pinBookToGrade/unpinBookFromGrade) e marca da escola
// (updateSchoolBranding) de fato salvam no Scolist. Tudo mais (segmento
// renomeado, temas, calibragem, didáticos, papelaria, "outros materiais",
// bimestres) só existia nesse slot único — e handleLogout apagava ele
// inteiro. Bug reportado pela Joana em vídeo no WhatsApp, 27/08/2026:
// renomeou um segmento, deu logout/login, voltou pro nome antigo.
//
// Fix: escopar o slot por conta (uid do Firebase Auth) — dar logout só
// limpa o estado em memória (pra tela de login aparecer limpa), não apaga
// o slot salvo; a mesma conta logando de novo recupera o progresso dela.
// O slot genérico antigo continua sendo escrito também, só por segurança
// (não é lido de volta depois que a conta resolve — ver loadScopedProgress).
//
// Escopado também por escola (account.school.id), não só por uid — com o
// seletor de escola no login (conta que atende mais de uma escola), o
// progresso de cada escola precisa ficar independente; senão trocar de
// escola pelo menu lateral mostrava a mesma lista pra qualquer uma delas
// (pedido explícito, 31/08/2026). "_none" é o fallback pra conta sem
// escola vinculada ainda.
function lsKeyFor(account) {
  const uid = account && account.user && account.user.id;
  if (!uid) return null;
  const schoolId = (account && account.school && account.school.id) || "_none";
  return `${LS_KEY}:${uid}:${schoolId}`;
}

function loadScopedProgress(account) {
  const scopedKey = lsKeyFor(account);
  if (!scopedKey) return null;
  try {
    return JSON.parse(localStorage.getItem(scopedKey) || "null");
  } catch (e) {
    return null;
  }
}

// O passo "Itens complementares" (steps-complementos.jsx) entrou na posição
// 13 do STEPS (shared.jsx), empurrando Confirmação final/Previsão de
// preço/Baixar lista uma casa pra frente. `current` é salvo como índice
// cru — sem isso, quem já estava num desses passos reabriria um passo
// antes (lista pronta parando nos complementos). Progresso salvo antes
// disso não tem a flag: soma 1 em quem estava do 13 em diante, uma vez só.
const STEP_COMPLEMENTOS_IDX = 13;
// Passos que saíram em 26/10/2026 por serem mock ("não podemos ter mais nada
// de mock"): "Lista do ano passado" (2, os ISBNs enviados não eram usados) e
// "Disponibilidade" (6, animação de estoque sem consulta nenhuma). Índices no
// formato com Complementos. Progresso parado num deles cai no passo seguinte.
const STEPS_REMOVED_NO_MOCK = [2, 6];
function migrateProgressSteps(current, data) {
  let d = data || {};
  let c = current;
  if (!d.__stepsComplementos) {
    c = typeof c === "number" && c >= STEP_COMPLEMENTOS_IDX ? c + 1 : c;
    d = { ...d, __stepsComplementos: true };
  }
  if (!d.__stepsNoMock) {
    if (typeof c === "number") c -= STEPS_REMOVED_NO_MOCK.filter((i) => i < c).length;
    d = { ...d, __stepsNoMock: true };
  }
  return { current: c, data: d };
}

function loadInitialProgress() {
  try {
    const saved = JSON.parse(localStorage.getItem(LS_KEY) || "{}") || {};
    return migrateProgressSteps(saved.current || 0, saved.data || {});
  } catch {
    return migrateProgressSteps(0, {});
  }
}

// window.CATALOG (catalog.js) é um array global mutável, nunca persistido
// — Step9_Recommend só empurra pra ele os livros da série que está sendo
// vista no momento (mais resultado de busca). Step11_Confirm e
// Step8_Pricing resolvem TODOS os livros pinados (de todas as séries)
// contra esse mesmo array via CATALOG.find(id) — então, depois de um
// refresh, qualquer série que a pessoa não tenha revisitado em
// Recomendações nesta sessão fica sem detalhe pra resolver, e o
// CATALOG.find(...).filter(Boolean) descarta o livro da lista mesmo com
// o id certinho em pinned[série]. Bug reportado pelo Alex, 27/08/2026:
// "não tá persistindo a mesma lista do passo 7 [Recomendações], no passo
// 13 [Confirmação final]".
//
// Fix: espelha as entradas "runtime" do CATALOG (id começando com "rt-"
// — o catálogo estático de catalog.js não precisa, já vem no arquivo)
// no mesmo slot de progresso por conta, e restaura no load.
//
// Só entram no snapshot os ids que algum campo persistido realmente
// referencia (data.pinned + data.searchHistory, ver
// collectReferencedRuntimeBookIds) — NÃO todo item que já passou pela tela
// nesta sessão. window.CATALOG é um array global mutável que só cresce:
// toda busca (manual, por tema, ou a lista de recomendação de cada série)
// empurra pra lá, e nada nunca é removido dele. Espelhar o array inteiro
// (como este snapshot fazia antes) faz o payload do wizard crescer sem
// limite conforme a pessoa navega/busca — Firestore tem limite de 1 MiB
// por documento (ver stripLargeFieldsForSync abaixo), e o autosave engole
// esse erro DE PROPÓSITO (rede de segurança do localStorage), então
// estourar o limite não dá erro visível nenhum: só para de sincronizar
// pro backend a partir dali, o que é indistinguível na tela de "não tá
// salvando". Bug relatado de novo pela Amanda/Julia (Eleva Barra,
// 21/09/2026, via WhatsApp): "a página voltou a não mostrar os itens no
// detalhe" (papelaria) e, à parte, "mexi numa lista e não reflete no
// computador da escola" — mesmo sintoma do fix original de 18/09
// (persistência do catálogo), só que causado pelo PRÓPRIO payload daquele
// fix crescendo até estourar o documento depois de uso normal contínuo.
function collectReferencedRuntimeBookIds(data) {
  const ids = new Set();
  const addAllSeries = (bySeries) => {
    Object.values(bySeries || {}).forEach((list) => {
      (Array.isArray(list) ? list : []).forEach((id) => { if (id) ids.add(id); });
    });
  };
  addAllSeries(data && data.pinned);
  addAllSeries(data && data.searchHistory);
  return ids;
}

function snapshotRuntimeCatalog(data) {
  if (!Array.isArray(window.CATALOG)) return [];
  const referenced = collectReferencedRuntimeBookIds(data);
  return window.CATALOG.filter((b) => b && typeof b.id === "string" && b.id.startsWith("rt-") && referenced.has(b.id));
}

// O nível Scolex salvo no snapshot nunca é confiável: progresso gravado antes
// de 07/10/2026 tem nível inventado (120 + índice*7) pra livro sem Scolex, e
// mesmo um nível real pode mudar quando o time do Scolex verifica o livro.
// Restaura sem nível e refreshRuntimeCatalogFromCatalog busca o valor atual
// (junto com título/autor/capa/etc., que também podem ter sido editados no
// Scolex depois do snapshot).
function restoreRuntimeCatalog(snapshot) {
  if (!Array.isArray(snapshot) || !Array.isArray(window.CATALOG)) return;
  snapshot.forEach((book) => {
    if (!book || !book.id) return;
    const restored = Object.assign({}, book, { level: null });
    const existing = window.CATALOG.find((b) => b.id === book.id);
    if (existing) Object.assign(existing, restored);
    else window.CATALOG.push(restored);
  });
}

// Atualiza os livros runtime do CATALOG com os dados atuais do catálogo
// compartilhado (mesmo valor do app Scolex): nível, título, autor, editora,
// capa, amostra e sinopse — ver applyCatalogBook em api.js. Resolve true se
// mudou algum.
const CATALOG_REFRESH_FIELDS = ["level", "title", "author", "publisher", "editora", "imageUrl", "coverFromCatalog", "sampleUrl", "summary"];
async function refreshRuntimeCatalogFromCatalog() {
  const api = window.ScolistAPI;
  if (!Array.isArray(window.CATALOG) || !api || typeof api.getCatalogBooksByIsbn !== "function") return false;
  const books = window.CATALOG.filter((b) => b && typeof b.id === "string" && b.id.startsWith("rt-") && b.isbn);
  if (books.length === 0) return false;
  const catalogByIsbn = await api.getCatalogBooksByIsbn(books.map((b) => b.isbn));
  let changed = false;
  books.forEach((b) => {
    const fresh = api.applyCatalogBook(b, catalogByIsbn.get(String(b.isbn).replace(/[^0-9Xx]/g, "")));
    // `editora` é o rótulo exibido (normalizeRuntimeBook, steps-curation.jsx).
    if (fresh.publisher && fresh.publisher !== b.publisher) fresh.editora = fresh.publisher;
    CATALOG_REFRESH_FIELDS.forEach((key) => {
      if (fresh[key] !== undefined && b[key] !== fresh[key]) { b[key] = fresh[key]; changed = true; }
    });
  });
  return changed;
}

// Mesmo problema do CATALOG acima, só que pra Papelaria: window.PAPELARIA_CATALOG
// também é um array global mutável, e o modo "Montar do zero" empurra pra
// lá os resultados de busca ao vivo na VTEX (ids "vtex-<sku>", ver
// searchStationeryVtex/mapStationeryResultToItem em api.js). data.papelaria
// só guarda o itemId — sem espelhar essas entradas "vtex-" no progresso
// salvo (igual já se fazia pro "rt-" da literatura), elas só existem na
// memória de quem buscou. Qualquer outra sessão (outro navegador, ou até
// essa mesma depois de um refresh) reabre com PAPELARIA_CATALOG vazio
// desses itens: o carrinho continua "com 26 itens" (contagem crua do
// array salvo), mas cada PAPELARIA_CATALOG.find(itemId) volta undefined e
// o item simplesmente não renderiza (nem entra na estimativa de preço,
// que some sozinha) — bug reportado por escola real (Eleva Barra,
// 18/09/2026): "adicionamos os itens mas não conseguimos ver dentro de
// cada série".
//
// Mesmo bound de snapshotRuntimeCatalog acima (e mesmo motivo): só os itens
// que aparecem em algum carrinho de data.papelaria entram no snapshot, não
// todo resultado que já apareceu numa busca "Montar do zero" nesta sessão.
// PAPELARIA_CATALOG cresce a cada busca (debounce de 350ms a cada
// keystroke, ver PapelariaBuilder em steps-papelaria.jsx) e nunca encolhe —
// sem este filtro, o próprio payload deste fix cresce sem limite conforme
// a pessoa pesquisa itens, até estourar o limite de 1 MiB do Firestore e
// reintroduzir o mesmo bug que ele corrigiu (ver collectReferencedRuntimeBookIds).
function snapshotRuntimePapelariaCatalog(data) {
  if (!Array.isArray(window.PAPELARIA_CATALOG)) return [];
  const referenced = new Set();
  Object.values((data && data.papelaria) || {}).forEach((cart) => {
    (Array.isArray(cart) ? cart : []).forEach((c) => { if (c && c.itemId) referenced.add(c.itemId); });
  });
  return window.PAPELARIA_CATALOG.filter((it) => it && typeof it.id === "string" && it.id.startsWith("vtex-") && referenced.has(it.id));
}

function restoreRuntimePapelariaCatalog(snapshot) {
  if (!Array.isArray(snapshot) || !Array.isArray(window.PAPELARIA_CATALOG)) return;
  snapshot.forEach((item) => {
    if (!item || !item.id) return;
    const existing = window.PAPELARIA_CATALOG.find((x) => x.id === item.id);
    if (existing) Object.assign(existing, item);
    else window.PAPELARIA_CATALOG.push(item);
  });
}

// Firestore tem limite de 1 MiB por documento. data.schoolLogoUrl pode ser
// um data:-URL de até ~700KB (ver uploadSchoolLogo em api.js) — somado à lista
// estouraria esse limite. A logo já persiste separadamente (school.logo,
// via updateSchoolBranding) e é lida de volta com prioridade correta em
// pages-familia.jsx/steps-final.jsx (account.school.logoUrl) — não precisa
// duplicar no payload sincronizado com o backend.
function stripLargeFieldsForSync(data) {
  if (!data) return data;
  const { schoolLogoUrl, schoolLogoName, ...rest } = data;
  return rest;
}

function buildWizardSyncPayload({ current, data, catalog, papelariaCatalog, updatedAt }) {
  return { current, data: stripLargeFieldsForSync(data), catalog, papelariaCatalog, updatedAt };
}

// Resolve o progresso do wizard pra uma conta+escola recém-conhecida —
// decide entre o slot local escopado por (uid, schoolId) (loadScopedProgress,
// só localStorage) e o documento remoto (wizardProgress/{uid}_{schoolId}),
// e cobre a migração de quem só tinha progresso local (ex: bug reportado
// pela Joana, 29/08/2026: lista montada no Computador A não aparecia no
// Computador B).
//
// Escopado por (uid, schoolId) — antes era só uid, então trocar de escola
// mostrava o mesmo progresso pra qualquer uma (pedido explícito,
// 31/08/2026: "é importante que as listas entre as escolas sejam
// independentes"). O backend (WizardProgressService) cobre a migração de
// quem já tinha progresso salvo antes de existir separação por escola.
//
// Devolve { progress, base, push }: `progress` é o estado inicial; `base` o
// updatedAt da versão do servidor em que ele se baseia (vai como
// baseUpdatedAt nos PUTs — ver sendWizardSave no App; undefined = não deu
// pra saber, PUT sai sem base como antes); `push` um payload local que
// precisa subir pro backend — quem chama enfileira na mesma corrente do
// autosave, nunca solto (senão o autosave seguinte sairia com a base de
// antes do push e levaria 409 da própria aba).
// 1. Backend indisponível (erro de rede/servidor) -> usa o local como está.
// 2. Backend responde null (nunca sincronizou) + existe local -> MIGRAÇÃO:
//    local é a fonte de verdade, sobe pro backend e usa como estado
//    inicial.
// 3. Backend responde null + sem local -> conta/escola nova, estado vazio.
// 4. Backend responde com progresso + local sem updatedAt (formato salvo
//    antes deste fix) -> usa remoto.
// 5. Backend responde com progresso + local.updatedAt mais novo -> usa
//    local e sobe pro backend — só se o remoto ainda é a mesma versão em
//    que o local se baseou (local.baseUpdatedAt). Se outra pessoa salvou
//    depois, subir o local reverteria a edição dela (ver
//    wizardProgressWriteGuard no backend) — usa o remoto e a edição local
//    que não chegou a subir fica de fora. Snapshot local de antes do
//    controle de concorrência (sem baseUpdatedAt) segue a regra antiga.
// 6. Caso contrário -> usa remoto.
async function resolveWizardProgress(account) {
  const uid = account && account.user && account.user.id;
  const local = loadScopedProgress(account);
  const empty = { current: null, data: null, catalog: null, papelariaCatalog: null };

  if (!uid) return { progress: local || empty, base: undefined, push: null };

  let remote = null;
  try {
    remote = await window.ScolistAPI.loadWizardProgress(uid, (account.school && account.school.id) || null);
  } catch (e) {
    console.warn("Falha ao buscar progresso do wizard no backend, usando cópia local (se houver):", e);
    return { progress: local || empty, base: local ? local.baseUpdatedAt : undefined, push: null };
  }

  if (!remote) {
    return { progress: local || empty, base: null, push: local ? buildWizardSyncPayload(local) : null };
  }

  const remoteBase = remote.updatedAt || null;
  if (!local || !local.updatedAt) return { progress: remote, base: remoteBase, push: null };

  const localTime = Date.parse(local.updatedAt) || 0;
  const remoteTime = Date.parse(remote.updatedAt) || 0;
  if (localTime > remoteTime) {
    const hasLocalBase = Object.prototype.hasOwnProperty.call(local, "baseUpdatedAt");
    if (!hasLocalBase || (local.baseUpdatedAt || null) === remoteBase) {
      return { progress: local, base: remoteBase, push: buildWizardSyncPayload(local) };
    }
    console.warn("Progresso local não sincronizado descartado: a lista foi alterada por outra pessoa depois dele.");
  }

  return { progress: remote, base: remoteBase, push: null };
}

// ============= PT | EN toggle (canto superior direito, fixo) =============
function LangToggle() {
  const [lang, setLangState] = useState(() => (window.I18N && window.I18N.getLang()) || "pt");
  useEffect(() => {
    const onChange = (e) => setLangState(e.detail.lang);
    window.addEventListener("langchange", onChange);
    return () => window.removeEventListener("langchange", onChange);
  }, []);
  const set = (l) => { if (window.I18N) window.I18N.setLang(l); };
  const pillBase = {
    padding: "5px 11px",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
    border: "none",
    background: "transparent",
    color: "var(--text-mute)",
    letterSpacing: 0.4,
    transition: "color .15s, background .15s",
    fontFamily: "inherit",
  };
  const activePill = {
    ...pillBase,
    background: "#fff",
    color: "var(--teal-dark)",
    boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
    borderRadius: 8,
  };
  return (
    <div data-no-i18n style={{
      position: "fixed",
      top: 14,
      right: 16,
      zIndex: 9999,
      display: "flex",
      alignItems: "center",
      background: "#eef4f5",
      border: "1px solid var(--border)",
      borderRadius: 10,
      padding: 3,
      boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
    }}>
      <button onClick={() => set("pt")} style={lang === "pt" ? activePill : pillBase}>PT</button>
      <button onClick={() => set("en")} style={lang === "en" ? activePill : pillBase}>EN</button>
    </div>
  );
}

function LoadingScreen() {
  return (
    <div style={{
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "var(--bg-light)",
      color: "var(--text-mute)",
      fontSize: 15,
      fontWeight: 600,
    }}>
      Carregando contexto da plataforma...
    </div>
  );
}

function translateLoginError(error) {
  const code = (error && error.code) || "";
  if (code.includes("auth/invalid-credential")) return "Email ou senha inválidos.";
  if (code.includes("auth/wrong-password")) return "Email ou senha inválidos.";
  if (code.includes("auth/user-not-found")) return "Usuário não encontrado.";
  if (code.includes("auth/too-many-requests")) return "Muitas tentativas. Tente novamente em alguns minutos.";
  if (code.includes("auth/network-request-failed")) return "Falha de rede. Verifique sua conexão.";
  return "Não foi possível fazer login agora. Tente novamente.";
}

function AccessGate({ onLoggedIn, onBack, notice }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const C = window.HOME_COLORS;

  const handleLogin = async (event) => {
    event.preventDefault();
    if (loading) return;

    setLoading(true);
    setError("");

    try {
      await window.ScolistAPI.login(email.trim(), password, rememberMe);
      const context = await window.ScolistAPI.getSetupContext();
      onLoggedIn(context);
      setPassword("");
    } catch (e) {
      console.error(e);
      setError(translateLoginError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: "100vh",
      display: "flex",
      flexDirection: "column",
      background: "#fff",
      fontFamily: C.font,
    }}>
      <div style={{ flex: 1, display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "center", gap: 40, padding: "28px 40px 16px" }}>

        <div style={{ flex: "0 1 420px", display: "flex", flexDirection: "column", justifyContent: "center" }}>
          <div style={{ width: "100%", maxWidth: 420 }}>
            {onBack ? (
              <button
                type="button"
                onClick={onBack}
                style={{ background: "none", border: "none", cursor: "pointer", padding: 0, marginBottom: 18, font: `600 14px/1 ${C.font}`, color: C.slate600, display: "inline-flex", alignItems: "center", gap: 6 }}
              >
                ← Voltar
              </button>
            ) : null}
            <img src="assets/scolist-wordmark-teal.png" alt="Scolist" style={{ height: 42, width: "auto", display: "block" }} />

            <div style={{ font: `600 13px/1.2 ${C.font}`, letterSpacing: "0.18em", textTransform: "uppercase", color: C.teal300, marginTop: 26 }}>Entrar</div>
            <h1 style={{ font: `600 32px/1.16 ${C.font}`, color: C.textStrong, margin: "12px 0 0" }}>Bom te ver de volta</h1>
            <p style={{ font: `400 17px/1.55 ${C.font}`, color: C.textBody, margin: "10px 0 0" }}>
              Escolas montam a lista do próximo ano com o Lex, série por série.
            </p>

            <form onSubmit={handleLogin} style={{ display: "grid", gap: 14, marginTop: 26 }}>
              <label style={{ display: "grid", gap: 8 }}>
                <span style={{ font: `600 14px/1.2 ${C.font}`, color: C.textStrong }}>E-mail institucional</span>
                <input
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  required
                  placeholder="coordenacao@suaescola.com.br"
                  style={{
                    width: "100%", boxSizing: "border-box", font: `400 16px/1.2 ${C.font}`, color: C.textStrong,
                    padding: "14px 18px", borderRadius: 999, background: "#fff", border: `1.5px solid ${C.borderHairline}`, outline: "none",
                  }}
                />
              </label>

              <div style={{ display: "grid", gap: 8 }}>
                <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12 }}>
                  <span style={{ font: `600 14px/1.2 ${C.font}`, color: C.textStrong }}>Senha</span>
                  <a href="#" onClick={(e) => e.preventDefault()} style={{ font: `500 13px/1.2 ${C.font}`, color: C.teal300 }}>Esqueci minha senha</a>
                </div>
                <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    required
                    placeholder="••••••••"
                    style={{
                      width: "100%", boxSizing: "border-box", font: `400 16px/1.2 ${C.font}`, color: C.textStrong,
                      padding: "14px 92px 14px 18px", borderRadius: 999, background: "#fff", border: `1.5px solid ${C.borderHairline}`, outline: "none",
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    style={{ position: "absolute", right: 8, border: "none", background: "transparent", cursor: "pointer", font: `600 13px/1 ${C.font}`, color: C.teal300, padding: "10px 12px", borderRadius: 999 }}
                  >
                    {showPassword ? "ocultar" : "mostrar"}
                  </button>
                </div>
              </div>

              <label style={{ display: "flex", alignItems: "center", gap: 8, font: `400 14px/1.2 ${C.font}`, color: C.slate600 }}>
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(event) => setRememberMe(event.target.checked)}
                />
                Manter sessão ativa neste navegador
              </label>

              {notice && !error && (
                <div role="status" style={{
                  background: "#fff8ec", border: "1px solid #f3d9a8", borderRadius: 10, padding: "10px 12px",
                  color: "#7a5a1e", font: `600 13px/1.4 ${C.font}`,
                }}>
                  {notice}
                </div>
              )}

              {error && (
                <div style={{
                  background: "#fff1f1", border: "1px solid #f3b2b2", borderRadius: 10, padding: "10px 12px",
                  color: "#8a2525", font: `600 13px/1.4 ${C.font}`,
                }}>
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                style={{
                  width: "100%", border: "none", cursor: loading ? "default" : "pointer", borderRadius: 999,
                  padding: "15px 26px", font: `600 16px/1 ${C.font}`,
                  background: loading ? C.teal100 : C.sun400, color: C.teal700,
                }}
              >
                {loading ? "Entrando..." : "Entrar"}
              </button>

              <p style={{ font: `400 14px/1.4 ${C.font}`, color: C.textBody, margin: 0, textAlign: "center" }}>
                Ainda não tem uma conta?{" "}
                {/* dashboard é um app/domínio separado deste (front-onboard) —
                    link absoluto, não uma rota interna daqui. Confirme se
                    este é o domínio certo do dashboard em produção. */}
                <a href="https://listaescolar.scolados.com/pre-registro" style={{ color: C.teal300, fontWeight: 600 }}>Solicite seu cadastro</a>
              </p>

              <p style={{ font: `400 14px/1.4 ${C.font}`, color: C.textBody, margin: 0, textAlign: "center" }}>
                <a href="https://wa.me/5511975055722?text=Quero%20saber%20mais%20sobre%20o%20Scolist" target="_blank" rel="noopener" style={{ color: C.teal300 }}>Fale com a equipe</a>
              </p>
            </form>
          </div>
        </div>

        <div style={{ flex: "0 1 360px", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", textAlign: "center" }}>
          <img src="assets/lex1.png" alt="Lex" style={{ width: "100%", maxWidth: 230, height: "auto", display: "block" }} />
          <p style={{ font: `600 24px/1.3 ${C.font}`, color: C.textStrong, margin: "16px 0 0", maxWidth: "20ch" }}>
            A lista de volta às aulas, montada nível por nível.
          </p>
          <p style={{ font: `400 15px/1.5 ${C.font}`, color: C.textBody, margin: "8px 0 0", maxWidth: "34ch" }}>
            Literatura, didáticos e papelaria em um link só, com a escala Scolex® por trás de cada indicação.
          </p>
        </div>

      </div>

      <div style={{ padding: "0 32px 18px" }}>
        <div style={{ font: `600 13px/1.2 ${C.font}`, letterSpacing: "0.18em", textTransform: "uppercase", color: C.slate600, textAlign: "center" }}>
          Editoras parceiras
        </div>
        <div style={{ marginTop: 14, maxWidth: 900, marginLeft: "auto", marginRight: "auto" }}>
          <PublisherLogos gap={16} />
        </div>
      </div>
    </div>
  );
}

// Tela de seleção de escola — aparece logo depois do login (ou ao restaurar
// sessão) quando a conta atende mais de uma escola. Antes disso, a conta
// caía silenciosamente na primeira escola em ordem alfabética
// (chooseSchool, api.js) — ninguém escolhia nada, então uma conta que
// atende duas escolas podia acabar editando/vendo a lista da escola errada
// sem perceber. A escolha feita aqui fica confirmada (escopada por uid, ver
// getConfirmedSchoolId/selectSchool em api.js) e não pergunta de novo nas
// próximas sessões — só reaparece via "trocar escola" (Shell, sidebar).
function SchoolPicker({ schools, currentSchoolId, onPick, onCancel, cancelLabel }) {
  const [picking, setPicking] = useState(null); // id da escola sendo confirmada
  const [error, setError] = useState("");

  const handlePick = async (schoolId) => {
    if (picking) return;
    setPicking(schoolId);
    setError("");
    try {
      await onPick(schoolId);
    } catch (e) {
      console.error(e);
      setError("Não foi possível confirmar essa escola agora. Tente de novo.");
      setPicking(null);
    }
  };

  return (
    <div style={{
      minHeight: "100vh",
      padding: "48px 20px 24px",
      background: "linear-gradient(180deg, #f0fbfb 0%, #e7f5f7 100%)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}>
      <div style={{
        width: "100%",
        maxWidth: 640,
        background: "#fff",
        border: "1px solid var(--border)",
        borderRadius: 16,
        padding: 24,
        boxShadow: "var(--shadow-sm)",
      }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: "var(--teal-dark)", textTransform: "uppercase", letterSpacing: 1 }}>
          Sua conta atende mais de uma escola
        </div>
        <h1 style={{ marginTop: 8, fontSize: 26, lineHeight: 1.15, letterSpacing: -0.5 }}>
          Qual escola você quer abrir?
        </h1>
        <p style={{ marginTop: 6, marginBottom: 18, color: "var(--text-mute)", fontSize: 14 }}>
          Você pode trocar de escola depois a qualquer momento pelo menu lateral.
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: 10, maxHeight: "50vh", overflowY: "auto", paddingRight: 4 }}>
          {schools.map((school) => {
            const isCurrent = school.id === currentSchoolId;
            const isPicking = picking === school.id;
            return (
              <button
                key={school.id}
                onClick={() => handlePick(school.id)}
                disabled={!!picking}
                style={{
                  display: "flex", alignItems: "center", gap: 14,
                  padding: "14px 16px", textAlign: "left",
                  border: `1.5px solid ${isCurrent ? "var(--teal)" : "var(--border)"}`,
                  background: isCurrent ? "var(--bg-light)" : "#fff",
                  borderRadius: 12,
                  cursor: picking ? "default" : "pointer",
                  opacity: picking && !isPicking ? 0.5 : 1,
                }}
              >
                <div style={{
                  width: 40, height: 40, borderRadius: school.logoUrl ? 8 : "50%",
                  background: school.logoUrl ? "#fff" : "var(--teal)",
                  color: "#fff", border: school.logoUrl ? "1px solid var(--border)" : "none",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  overflow: "hidden", flexShrink: 0, fontSize: 14, fontWeight: 700,
                }}>
                  {school.logoUrl
                    ? <img src={school.logoUrl} alt={school.name} style={{ maxWidth: "82%", maxHeight: "82%", objectFit: "contain" }}/>
                    : (school.name || "?").split(/\s+/).map(w => w[0]).filter(Boolean).slice(0, 2).join("").toUpperCase()}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: "var(--text)" }}>{school.name || "Escola sem nome"}</div>
                  {isCurrent && <div style={{ fontSize: 11.5, color: "var(--teal-dark)", fontWeight: 600, marginTop: 2 }}>Escola atual</div>}
                </div>
                <div style={{ fontSize: 13, fontWeight: 700, color: "var(--teal-dark)", flexShrink: 0 }}>
                  {isPicking ? "Abrindo…" : "Selecionar →"}
                </div>
              </button>
            );
          })}
        </div>

        {error && (
          <div style={{
            marginTop: 14,
            background: "#fff1f1", border: "1px solid #f3b2b2",
            borderRadius: 10, padding: "10px 12px",
            color: "#8a2525", fontSize: 13, fontWeight: 600,
          }}>
            {error}
          </div>
        )}

        {onCancel && (
          <div style={{ marginTop: 18, textAlign: "center" }}>
            <button onClick={onCancel} disabled={!!picking} style={{
              background: "transparent", border: "none",
              color: "var(--text-mute)", fontSize: 13, fontWeight: 600,
              cursor: picking ? "default" : "pointer",
            }}>
              {cancelLabel || "Cancelar"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// Toast de 401/403 (evento "scolist:auth-error", disparado por scolistFetch
// em api.js). As rotas de escrita da escola passaram a exigir token
// (functions/lib/routes/schoolRoutes.js) — sem isso, uma sessão vencida
// fazia a alteração (ex.: cor da escola) simplesmente não salvar, calada.
// Um toast por vez; some sozinho, menos o de sessão vencida, que só sai
// entrando de novo ou fechando.
function AuthErrorToast() {
  const [toast, setToast] = useState(null); // { status }
  useEffect(() => {
    let hide = null;
    const onError = (e) => {
      const status = (e.detail && e.detail.status) || 401;
      setToast({ status });
      clearTimeout(hide);
      if (status === 403) hide = setTimeout(() => setToast(null), 8000);
    };
    window.addEventListener("scolist:auth-error", onError);
    return () => { window.removeEventListener("scolist:auth-error", onError); clearTimeout(hide); };
  }, []);
  if (!toast) return null;
  const expired = toast.status === 401;
  const relogin = async () => {
    try { await window.ScolistAPI.logout(); } catch (e) { /* recarrega mesmo assim */ }
    window.location.reload();
  };
  return (
    <div role="alert" style={{
      position: "fixed", top: 20, right: 20, zIndex: 1001,
      width: 360, maxWidth: "calc(100vw - 32px)", boxSizing: "border-box",
      padding: "12px 14px", borderRadius: 10, background: "#fdecec", border: "1px solid #e5a3a3",
      boxShadow: "0 6px 24px rgba(0,0,0,.14)", color: "#6b1d1d", fontSize: 14, lineHeight: 1.45,
    }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, marginBottom: 2 }}>{expired ? "Sessão expirada" : "Sem permissão"}</div>
          {expired
            ? "Sua última alteração não foi salva. Entre de novo para continuar."
            : "Sua conta não tem permissão para essa alteração. Nada foi salvo."}
        </div>
        <button type="button" onClick={() => setToast(null)} aria-label="Fechar aviso" style={{
          background: "none", border: "none", fontSize: 16, color: "#6b1d1d", cursor: "pointer", padding: 0, lineHeight: 1,
        }}>✕</button>
      </div>
      {expired && (
        <button type="button" onClick={relogin} style={{
          marginTop: 10, padding: "7px 14px", borderRadius: 8, border: "none", cursor: "pointer",
          background: "#b42318", color: "#fff", fontWeight: 600, fontSize: 13.5,
        }}>Entrar de novo</button>
      )}
    </div>
  );
}

// Aviso do 409 do autosave (ver sendWizardSave no App): a lista desta aba
// ficou desatualizada e nada mais dela vai pro backend até recarregar.
// Recarregar aplica a versão do servidor; a edição local que levou o 409
// NÃO é reaplicada por cima — o autosave manda a lista inteira, não a
// edição em si, então não há como separar "o que esta aba mudou" do que
// ela simplesmente tinha de velho sem arriscar reverter a outra pessoa de
// novo (que é justamente o bug que isso evita).
function SyncConflictBanner({ onReload }) {
  const [busy, setBusy] = useState(false);
  const reload = async () => {
    setBusy(true);
    try { await onReload(); } finally { setBusy(false); }
  };
  return (
    <div role="alert" style={{
      position: "fixed", bottom: 20, left: "50%", transform: "translateX(-50%)", zIndex: 1000,
      width: "max-content", maxWidth: "calc(100vw - 32px)", boxSizing: "border-box",
      display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap",
      padding: "12px 16px", borderRadius: 10, background: "#fff4e5", border: "1px solid #f0b45c",
      boxShadow: "0 6px 24px rgba(0,0,0,.14)", color: "#5c3b00", fontSize: 14, lineHeight: 1.4,
    }}>
      <span style={{ flex: "1 1 260px" }}>Esta lista foi alterada por outra pessoa. Recarregue para ver a versão atual.</span>
      <button type="button" onClick={reload} disabled={busy} style={{
        padding: "8px 16px", borderRadius: 8, border: "none", cursor: busy ? "default" : "pointer",
        background: "var(--teal)", color: "#fff", fontWeight: 600, fontSize: 14, opacity: busy ? 0.7 : 1,
      }}>{busy ? "Recarregando…" : "Recarregar"}</button>
    </div>
  );
}

function App() {
  const [current, setCurrent] = useState(() => loadInitialProgress().current);
  const [data, setData] = useState(() => loadInitialProgress().data);
  // Qualquer data que nasce depois (logout, escola nova) já está no
  // formato de passos novo — ver migrateProgressSteps.
  useEffect(() => {
    if (!data.__stepsComplementos || !data.__stepsNoMock) {
      setData(d => (d.__stepsComplementos && d.__stepsNoMock ? d : { ...d, __stepsComplementos: true, __stepsNoMock: true }));
    }
  }, [data]);

  const [account, setAccount] = useState(null);
  const [authResolved, setAuthResolved] = useState(false);
  // Visitante não-logado vê a Home institucional primeiro; "Entrar" (ou
  // qualquer CTA da Home) revela o AccessGate. Volta pra Home no "← Voltar"
  // do login ou depois de um logout.
  //
  // /login é rota de verdade (não só um parâmetro) — dá pra mandar esse link
  // pronto direto pra um cliente específico. GH Pages não tem servidor pra
  // reescrever rota nenhuma: front-onboard/404.html (cópia de index.html,
  // com <base href="/"> pra todo asset resolver a partir da raiz mesmo
  // carregando de /login) é o que faz o link abrir o app normalmente em vez
  // de dar 404 de verdade. ?login=1 continua sendo aceito na leitura inicial
  // só por compatibilidade com link antigo já compartilhado.
  const isLoginPath = () => {
    try { return location.pathname.replace(/\/+$/, "") === "/login"; } catch (e) { return false; }
  };
  const [showLogin, setShowLogin] = useState(() => {
    if (isLoginPath()) return true;
    try { return new URLSearchParams(location.search).has("login"); } catch (e) { return false; }
  });

  // pushState (não replaceState) pra "Entrar"/"← Voltar" navegarem de
  // verdade entre / e /login — o botão voltar do navegador soma sozinho
  // (ver popstate logo abaixo).
  const goToLogin = () => {
    try { if (!isLoginPath()) history.pushState(null, "", "/login"); } catch (e) {}
    setShowLogin(true);
  };
  const goBackToHome = () => {
    try { if (isLoginPath()) history.pushState(null, "", "/"); } catch (e) {}
    // Vindo do #admin a Home é pulada (ver !isAdminHash abaixo) — sem
    // limpar o hash, o "Voltar" do login não sairia do lugar.
    if (/^#\/?admin/.test(location.hash)) location.hash = "";
    setShowLogin(false);
  };
  useEffect(() => {
    const onPopState = () => setShowLogin(isLoginPath());
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // { schools, selectedSchoolId } quando a conta atende mais de uma escola
  // e ainda precisa escolher (needsSchoolPick do backend) ou quando a
  // pessoa pediu pra trocar de escola pelo menu (ver switchSchool mais
  // abaixo) — null quando não há seleção pendente.
  const [pendingSchoolPick, setPendingSchoolPick] = useState(null);
  // true durante completeLogin (login, restauração de sessão, ou troca de
  // escola) — setAccount roda antes do await de resolveWizardProgress, o
  // que causa um render intermediário com a escola NOVA mas current/data
  // ainda da escola ANTIGA. Sem essa trava, o efeito de autosave (mais
  // abaixo) veria essa combinação inconsistente e poderia salvar o
  // progresso antigo sob a chave da escola nova. A guarda de debounce já
  // resolveria isso na prática (o próximo render, com os dados certos,
  // cancela esse timer errado antes dele disparar), mas essa trava fecha
  // a janela por completo em vez de depender só do timing.
  const isLoadingProgressRef = useRef(false);

  const persistRuntimeContext = (context) => {
    if (!context || !context.account) return;

    const schools = Array.isArray(context.schools) ? context.schools : [];
    const grades = Array.isArray(context.grades) ? context.grades : [];

    const schoolId = context.selectedSchoolId || (schools[0] && schools[0].id) || "";
    const school = schools.find((each) => each.id === schoolId) || schools[0] || null;

    const gradeId = context.selectedGradeId || (grades[0] && grades[0].id) || "";
    const grade = grades.find((each) => each.id === gradeId) || grades[0] || null;

    setData((prev) => ({
      ...prev,
      schoolName: (school && school.name) || prev.schoolName,
      runtimeSelection: {
        ...(prev.runtimeSelection || {}),
        availableSchools: schools,
        availableGrades: grades,
        schoolId,
        schoolName: (school && school.name) || "",
        period: context.period ? String(context.period) : (school && school.currentPeriod ? String(school.currentPeriod) : ""),
        gradeId,
        gradeName: (grade && (grade.name || grade.id)) || "",
        updatedAt: new Date().toISOString(),
      },
    }));
  };

  // Termina o login/restauração de sessão a partir de um contexto já
  // resolvido pra uma única escola (needsSchoolPick false — ou porque só
  // tem uma, ou porque a pessoa acabou de escolher no SchoolPicker).
  // Compartilhada entre o efeito de mount (restaura sessão) e o
  // onLoggedIn do AccessGate (login explícito) pra não duplicar essa
  // sequência em dois lugares.
  // Didáticos adotados antes de existir o snapshot na adoção (ver
  // resolveDidatico em steps-didaticos.jsx) só têm o itemId salvo — sem
  // isso, depois de um refresh o contador da série dizia "1 livro" e a
  // lista ficava vazia. Busca esses livros pelo id no backend e registra
  // só no cache em memória (DIDATICOS_RUNTIME); o bump abaixo existe apenas
  // pra re-renderizar com os itens recuperados. NÃO mexe em `data` de
  // propósito: mudar `data` aqui dispararia o autosave sozinho, sem edição
  // nenhuma da pessoa, e poderia sobrescrever uma edição feita por outra
  // conta da mesma escola nesse meio tempo. O snapshot é gravado junto na
  // próxima edição real (ver attachDidaticoSnapshots no autosave).
  const [, setDidaticosHydration] = useState(0);
  // Mesmo esquema (contador só pra re-renderizar, sem mexer em `data`) pros
  // dados atuais (nível, título, capa...) dos livros restaurados — ver
  // refreshRuntimeCatalogFromCatalog.
  const [, setCatalogDataRefresh] = useState(0);
  const refreshCatalogDataInBackground = () => {
    refreshRuntimeCatalogFromCatalog()
      .then((changed) => { if (changed) setCatalogDataRefresh((n) => n + 1); })
      .catch((e) => console.warn("Falha ao atualizar dados dos livros pelo catálogo:", e));
  };
  const hydrateDidaticosInBackground = (loadedData) => {
    if (!loadedData || typeof hydrateDidaticosRuntime !== "function") return;
    hydrateDidaticosRuntime(loadedData)
      .then((recovered) => { if (recovered > 0) setDidaticosHydration((n) => n + 1); })
      .catch((e) => console.warn("Falha ao reidratar didáticos adotados:", e));
  };

  const completeLogin = async (context) => {
    isLoadingProgressRef.current = true;
    try {
      const nextAccount = (context && context.account) || null;
      setAccount(nextAccount);
      // Sobrepõe o que foi lido do slot genérico no primeiro render (pode
      // ser de outra conta/escola que usou esse navegador por último) pelo
      // progresso resolvido dessa conta+escola — local ou do backend, o
      // que estiver mais atualizado (ver resolveWizardProgress).
      const { progress: resolved, base, push } = await resolveWizardProgress(nextAccount);
      setWizardBase(nextAccount, base);
      setSyncConflict(null);
      if (push) {
        enqueueWizardSave(nextAccount.user.id, (nextAccount.school && nextAccount.school.id) || null, push).catch((e) => {
          console.warn("Falha ao subir progresso local pro backend:", e);
        });
      }
      // Sempre define current/data explicitamente (nunca deixa como
      // estava) — completeLogin também roda ao trocar de escola pelo
      // menu (switchSchool), e uma escola sem progresso salvo ainda
      // (resolved.current/data null) precisa começar vazia, não herdar o
      // que estava na tela da escola anterior.
      const migrated = migrateProgressSteps(typeof resolved.current === "number" ? resolved.current : 0, resolved.data || {});
      setCurrent(migrated.current);
      setData(migrated.data);
      restoreRuntimeCatalog(resolved.catalog);
      refreshCatalogDataInBackground();
      restoreRuntimePapelariaCatalog(resolved.papelariaCatalog);
      hydrateDidaticosInBackground(resolved.data);
      if (context && context.account) persistRuntimeContext(context);
    } finally {
      isLoadingProgressRef.current = false;
    }
  };

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const context = await window.ScolistAPI.getSetupContext();
        if (cancel) return;
        if (context && context.needsSchoolPick) {
          setPendingSchoolPick({ schools: context.schools, selectedSchoolId: context.selectedSchoolId });
          return;
        }
        await completeLogin(context);
      } catch (e) {
        if (!cancel) {
          setAccount(null);
          console.warn("Falha ao carregar conta:", e);
        }
      } finally {
        if (!cancel) setAuthResolved(true);
      }
    })();
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Hash-based route for family-facing pages (#familia, #familia/serie/3, etc.)
  const [hash, setHashState] = useState(() => (location.hash || "").replace(/^#\/?/, ""));
  useEffect(() => {
    const onHash = () => setHashState((location.hash || "").replace(/^#\/?/, ""));
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);
  const setHash = (h) => { location.hash = "#" + h; window.scrollTo({ top: 0 }); };

  // Link público da lista pras famílias — list.scolex.com.br/<slug>[/<série>]
  // (ex.: /eleva-barra/5-ano, ver Step13_Link e PublicSlugService no
  // backend). Formato antigo #lista/<schoolId>[/serie/<n>] continua aceito
  // e é reescrito pro endereço amigável assim que a lista carrega. Busca os
  // dados direto do backend público (sem auth, sem esperar a sessão
  // resolver), diferente do preview interno #familia que usa o `data` em
  // memória de uma sessão logada. Ver
  // WizardProgressController.getPublicFamilyList.
  //
  // Caminho de verdade (não hash) funciona no GH Pages pelo mesmo truque do
  // /login: 404.html é cópia do index.html. O backend recusa slugs iguais a
  // pastas/rotas do site (RESERVED em PublicSlugService).
  const readPublicPath = () => {
    try {
      const parts = location.pathname.split("/").filter(Boolean).map(decodeURIComponent);
      if (!parts.length || parts.length > 2 || parts[0] === "login") return null;
      if (!/^[a-z0-9-]+$/i.test(parts[0])) return null;
      return { key: parts[0].toLowerCase(), serie: (parts[1] || "").toLowerCase() || null };
    } catch (e) { return null; }
  };
  const [publicPath, setPublicPath] = useState(readPublicPath);
  useEffect(() => {
    const onPop = () => setPublicPath(readPublicPath());
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const legacyPublic = hash.match(/^lista\/([^/]+)(?:\/serie\/(\d+))?/);
  const publicKey = (publicPath && publicPath.key) || (legacyPublic && legacyPublic[1]) || null;
  const [publicListState, setPublicListState] = useState({ status: "loading", data: null, schoolId: null, slug: null });
  useEffect(() => {
    if (!publicKey) return;
    let cancel = false;
    setPublicListState({ status: "loading", data: null, schoolId: null, slug: null });
    window.ScolistAPI.getPublicFamilyList(publicKey)
      .then((res) => {
        if (cancel) return;
        if (!res) { setPublicListState({ status: "not-found", data: null, schoolId: null, slug: null }); return; }
        restoreRuntimeCatalog(res.catalog);
        refreshCatalogDataInBackground();
        restoreRuntimePapelariaCatalog(res.papelariaCatalog);
        setPublicListState({ status: "ready", data: res.data || {}, schoolId: res.schoolId || publicKey, slug: res.slug || null });
        hydrateDidaticosInBackground(res.data);
      })
      .catch(() => { if (!cancel) setPublicListState({ status: "not-found", data: null, schoolId: null, slug: null }); });
    return () => { cancel = true; };
  }, [publicKey]);

  // Índice da série aberta no link público: slug (/eleva-barra/5-ano),
  // índice do formato antigo (#lista/<id>/serie/3), ou null = hub.
  // `undefined` = slug de série que não existe (cai no hub).
  const publicSeriesIdx = (() => {
    if (publicListState.status !== "ready") return null;
    if (publicPath && publicPath.serie) {
      const idx = window.seriesSlugs(publicListState.data).indexOf(publicPath.serie);
      return idx >= 0 ? idx : undefined;
    }
    if (!publicPath && legacyPublic && legacyPublic[2] != null) return parseInt(legacyPublic[2], 10);
    return null;
  })();
  const publicUrlFor = (seriesIdx) => {
    const slug = publicListState.slug || publicListState.schoolId;
    const serie = seriesIdx == null ? null : window.seriesSlugs(publicListState.data)[seriesIdx];
    return serie ? `/${slug}/${serie}` : `/${slug}`;
  };
  // Reescreve pro endereço canônico: link antigo com hash/schoolId, slug
  // antigo (alias de quando a escola trocou o endereço) ou série inexistente.
  useEffect(() => {
    if (publicListState.status !== "ready" || !publicListState.slug) return;
    const canonical = publicUrlFor(publicSeriesIdx === undefined ? null : publicSeriesIdx);
    if (location.pathname !== canonical || location.hash) {
      try { history.replaceState(null, "", canonical); } catch (e) {}
      setPublicPath(readPublicPath());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publicListState, publicSeriesIdx]);
  // Navegação dentro do link público (hub ↔ série). pages-familia.jsx fala
  // em rotas "lista/<schoolId>[/serie/<i>]"; aqui vira caminho amigável.
  const goPublic = (route) => {
    const m = String(route).match(/\/serie\/(\d+)$/);
    const url = publicListState.slug ? publicUrlFor(m ? parseInt(m[1], 10) : null) : "#" + route;
    try { history.pushState(null, "", url); } catch (e) {}
    if (publicListState.slug) setPublicPath(readPublicPath()); else setHashState(route);
    window.scrollTo({ top: 0 });
  };

  // GA (src/analytics.js): o wizard não muda de URL, então cada passo aberto
  // por uma escola logada vira um evento — é o que dá o funil por passo.
  const schoolIdForGa = account && account.school && account.school.id;
  useEffect(() => {
    if (!account || publicKey || !STEPS[current]) return;
    const step = STEPS[current];
    window.ScolistAnalytics.event("wizard_step", {
      step_index: current,
      step_name: typeof step.label === "string" ? step.label : step.short,
      step_group: step.group,
      school_id: schoolIdForGa || undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current, !!account, schoolIdForGa]);

  // Payload do autosave ainda não confirmado no backend — atualizado a cada
  // rodada do efeito abaixo, limpo quando o PUT (normal ou de emergência)
  // é enviado. Existe só pro flush de emergência (pagehide/visibilitychange
  // logo abaixo) saber o que reenviar se a aba fechar antes do debounce de
  // 350ms dar tempo de disparar sozinho. pendingSyncTimeoutRef guarda o
  // handle do setTimeout em si, só pra o flush poder cancelá-lo — sem
  // isso, depois de um flush de emergência bem sucedido o debounce
  // original ainda dispararia sozinho 300ms depois e reenviaria o mesmo
  // payload de novo (inofensivo, mas escrita/leitura à toa no Firestore).
  const pendingSyncRef = useRef(null);
  const pendingSyncTimeoutRef = useRef(null);
  // Encadeia os PUTs de saveWizardProgress um atrás do outro (nunca dois em
  // voo ao mesmo tempo) — sem isso, duas edições feitas com mais de 350ms
  // de intervalo (tempo o bastante pro debounce dar dois PUTs independentes,
  // não é só um clique duplo) geram dois requests concorrentes, e a rede
  // não garante que cheguem no servidor na mesma ordem em que saíram. O
  // servidor grava data:{...} inteiro por cima (merge:true é raso, não
  // por-campo-de-dentro-de-data) e carimba updatedAt com a hora DELE, não a
  // do payload — então, se o PUT da edição mais ANTIGA chegar por último,
  // ele sobrescreve a edição mais NOVA e ainda fica marcado como "mais
  // recente" (bate o updatedAt), fazendo resolveWizardProgress (próximo
  // load) escolher esse conteúdo velho como se fosse o atual. Explica os
  // dois sintomas revertendo JUNTOS (nome de série editado + livros
  // pinados) — os dois moram dentro do mesmo `data`, é o documento inteiro
  // que volta pra um estado anterior, não dois bugs separados. Bug
  // relatado pela Julia (Eleva Barra, 21/09/2026): "depois que eu esperei
  // um tempo desregulou tudo de novo". Encadear aqui garante que o PUT de
  // uma edição só sai depois que o da edição anterior já terminou — nunca
  // mais dois em voo disputando ordem de chegada.
  const saveChainRef = useRef(Promise.resolve());

  // Controle de concorrência otimista do autosave (ver
  // wizardProgressWriteGuard no backend): updatedAt da versão do servidor em
  // que a lista desta aba se baseia — vem do load (completeLogin) e avança a
  // cada PUT aceito. Vai como baseUpdatedAt em todo PUT; se outra pessoa
  // salvou nesse meio-tempo, o backend responde 409 em vez de deixar esta
  // aba (com a lista inteira desatualizada) reverter a edição dela. Bug da
  // Eleva Botafogo/Barra (14/10/2026): itens removidos por uma conta
  // voltavam "sozinhos" porque uma aba aberta havia horas em outra conta
  // salvava o estado inteiro por cima. `key` é o slot escopado (lsKeyFor)
  // da escola a que a base pertence — PUT ainda na fila de outra escola
  // (trocou de escola no meio) não usa a base da escola nova. value
  // undefined = base desconhecida (load sem backend): o PUT sai sem base,
  // como antes, e a primeira resposta aceita passa a ser a base.
  const wizardBaseRef = useRef({ key: null, value: undefined });
  const setWizardBase = (acc, value) => { wizardBaseRef.current = { key: lsKeyFor(acc), value }; };
  const withWizardBase = (key, payload) => {
    const base = wizardBaseRef.current;
    return base.key === key && base.value !== undefined ? { ...payload, baseUpdatedAt: base.value } : payload;
  };

  // Conflito pendente — enquanto existir, nada desta aba vai pro backend
  // (nem a fila, nem o flush de emergência) até a pessoa clicar em
  // Recarregar. Ref pra corrente de PUTs enxergar na hora; state pro aviso
  // aparecer. wizardEpochRef sobe a cada conflito: PUTs que já estavam na
  // fila carregam a lista velha e nunca podem sair, nem depois de
  // recarregar (com a base nova eles seriam aceitos e reverteriam tudo).
  const syncConflictRef = useRef(null);
  const [syncConflict, setSyncConflictState] = useState(null);
  const wizardEpochRef = useRef(0);
  const setSyncConflict = (conflict) => {
    if (conflict) wizardEpochRef.current += 1;
    syncConflictRef.current = conflict;
    setSyncConflictState(conflict);
  };

  // O snapshot do localStorage guarda a base que valia quando foi gravado
  // (ver autosave abaixo). Um PUT aceito com essa mesma base prova que o
  // snapshot (que já inclui essa edição, ou é posterior a ela nesta aba)
  // deriva da versão nova — sem isso, uma edição que só chegou ao
  // localStorage (aba fechou antes do PUT) seria descartada no próximo
  // load como se viesse de uma lista desatualizada (resolveWizardProgress).
  const advanceSnapshotBase = (key, sentBase, newBase) => {
    try {
      const snap = JSON.parse(localStorage.getItem(key) || "null");
      if (snap && snap.baseUpdatedAt === sentBase) {
        localStorage.setItem(key, JSON.stringify({ ...snap, baseUpdatedAt: newBase }));
      }
    } catch (e) {}
  };

  // Um PUT do autosave com a base atual. Aceito -> a base avança pro
  // updatedAt gravado (PUTs em sequência rápida da mesma aba saem um atrás
  // do outro pela saveChainRef, cada um com a base da resposta anterior).
  // 409 -> NÃO reenvia: guarda o progresso atual que veio junto e mostra o
  // aviso (SyncConflictBanner).
  const sendWizardSave = (uid, schoolId, payload) => {
    if (syncConflictRef.current) return Promise.resolve(null);
    const key = `${LS_KEY}:${uid}:${schoolId || "_none"}`;
    const body = withWizardBase(key, payload);
    return window.ScolistAPI.saveWizardProgress(uid, schoolId, body).then((res) => {
      if (res && !res.rejected && res.updatedAt && wizardBaseRef.current.key === key) {
        wizardBaseRef.current = { key, value: res.updatedAt };
        advanceSnapshotBase(key, body.baseUpdatedAt, res.updatedAt);
      }
      return res;
    }, (error) => {
      if (error && error.status === 409) {
        if (wizardBaseRef.current.key === key) {
          setSyncConflict({ uid, schoolId, progress: (error.data && error.data.progress) || null });
        }
        return null;
      }
      throw error;
    });
  };

  const enqueueWizardSave = (uid, schoolId, payload) => {
    const epoch = wizardEpochRef.current;
    // .catch(()=>{}) antes do .then: uma falha no PUT anterior não pode
    // travar a corrente pros próximos — só isolamos a ordem de saída.
    saveChainRef.current = saveChainRef.current
      .catch(() => {})
      .then(() => (epoch === wizardEpochRef.current ? sendWizardSave(uid, schoolId, payload) : null));
    return saveChainRef.current;
  };

  useEffect(() => {
    // Durante completeLogin (login, restauração, ou troca de escola),
    // account já pode ter mudado pra escola nova mas current/data ainda
    // são da escola antiga (setAccount roda antes do await) — não grava
    // nada enquanto isso, senão salvaria o progresso errado sob a chave
    // da escola nova. Ver isLoadingProgressRef acima.
    if (isLoadingProgressRef.current) return;

    // catalog/papelariaCatalog vão junto — snapshotRuntimeCatalog()/
    // snapshotRuntimePapelariaCatalog() leem window.CATALOG/PAPELARIA_CATALOG
    // na hora (não são React state, não disparam esse efeito sozinhos, mas
    // mudam toda vez que data.pinned/data.papelaria mudam também, então "de
    // carona" nesse efeito já cobre os momentos certos).
    const updatedAt = new Date().toISOString();
    const catalog = snapshotRuntimeCatalog(data);
    const papelariaCatalog = snapshotRuntimePapelariaCatalog(data);
    // Completa com `snapshot` as adoções de didáticos antigas que já
    // resolvem em memória (só adiciona o campo, nunca remove nada) — assim
    // elas passam a sobreviver a refresh sem depender do backend de livros.
    const dataForSync = typeof attachDidaticoSnapshots === "function" ? attachDidaticoSnapshots(data) : data;
    const scopedKey = lsKeyFor(account);
    // baseUpdatedAt junto do snapshot local: resolveWizardProgress só sobe
    // esse snapshot de volta se o servidor ainda estiver nessa versão.
    const base = wizardBaseRef.current;
    const baseUpdatedAt = scopedKey && base.key === scopedKey ? base.value : undefined;
    const snapshot = JSON.stringify({ current, data: dataForSync, catalog, papelariaCatalog, updatedAt, baseUpdatedAt });
    localStorage.setItem(LS_KEY, snapshot);
    if (scopedKey) localStorage.setItem(scopedKey, snapshot);

    const uid = account && account.user && account.user.id;
    if (!uid) return;
    const schoolId = (account && account.school && account.school.id) || null;

    // Autosave debounced pro backend — era 350ms (mesmo padrão do debounce
    // de busca ao digitar, ver steps-curation.jsx), reduzido pra 60ms:
    // pedido explícito depois do bug de corrida (Julia, Eleva Barra,
    // 21/09/2026) — "garantir o envio assim que for feita a alteração".
    // Ainda existe um debounce mínimo (não é 0ms) só pra coalescer múltiplas
    // mudanças de estado que acontecem no mesmo instante (ex.: React
    // batchando vários setData de um mesmo clique) num único PUT, em vez de
    // um PUT por mudança individual. A ordem de chegada em si já não
    // depende mais deste número: saveChainRef (abaixo) serializa os PUTs
    // desta aba, e o backend (WizardProgressService.saveWizardProgress)
    // agora rejeita qualquer escrita mais velha que a já salva, não importa
    // de onde venha. O localStorage acima já persistiu na hora; isso só
    // empurra pro backend pra sincronizar entre dispositivos (bug reportado
    // pela Joana, 29/08/2026: lista montada no Computador A não aparecia no
    // B).
    const payload = buildWizardSyncPayload({ current, data: dataForSync, catalog, papelariaCatalog, updatedAt });
    pendingSyncRef.current = { uid, schoolId, payload };
    const handle = setTimeout(() => {
      // Erro de rede vai pro warn de cada PUT (abaixo); a corrente em si
      // segue (ver enqueueWizardSave).
      enqueueWizardSave(uid, schoolId, payload)
        .then(() => {
          if (pendingSyncRef.current && pendingSyncRef.current.payload === payload) pendingSyncRef.current = null;
        })
        .catch((error) => {
          // Falha silenciosa de propósito: o localStorage já persistiu acima
          // como rede de segurança local — só perde a sincronização entre
          // dispositivos até o próximo autosave bem sucedido.
          console.warn("Falha ao sincronizar progresso do wizard com o backend:", error);
        });
    }, 60);
    pendingSyncTimeoutRef.current = handle;
    return () => clearTimeout(handle);
  }, [current, data, account]);

  // Empurra pro backend a edição ainda no debounce e espera a fila de PUTs
  // terminar — usado pelo Histórico (history-panel.jsx) antes de ler o log,
  // salvar uma versão ou restaurar, pra operar sobre o estado mais recente.
  const flushWizardSave = () => {
    const pending = pendingSyncRef.current;
    if (pending) {
      clearTimeout(pendingSyncTimeoutRef.current);
      enqueueWizardSave(pending.uid, pending.schoolId, pending.payload)
        .then(() => { if (pendingSyncRef.current === pending) pendingSyncRef.current = null; });
    }
    return saveChainRef.current.catch(() => {});
  };

  // Aplica na tela uma versão restaurada pelo backend (que já gravou ela
  // como progresso atual da escola). Mantém o que é da sessão, não da
  // lista: logo (nem vai pro backend, ver stripLargeFieldsForSync) e o
  // contexto de escola/série selecionada. O autosave que esse setData
  // dispara regrava o mesmo conteúdo — sem diferença, não gera log.
  const applyRestoredProgress = (progress) => {
    if (!progress) return;
    // O que veio do servidor é a versão atual dele — vira a base dos
    // próximos PUTs (ver sendWizardSave).
    setWizardBase(account, progress.updatedAt || null);
    restoreRuntimeCatalog(progress.catalog);
    refreshCatalogDataInBackground();
    restoreRuntimePapelariaCatalog(progress.papelariaCatalog);
    setData((prev) => Object.assign({}, progress.data || {}, {
      __stepsComplementos: true,
      __stepsNoMock: true,
      schoolLogoUrl: prev.schoolLogoUrl,
      schoolLogoName: prev.schoolLogoName,
      runtimeSelection: prev.runtimeSelection,
    }));
    hydrateDidaticosInBackground(progress.data);
  };

  // Botão Recarregar do aviso de conflito: busca a versão mais nova (a que
  // veio no 409 já pode ter ficado velha) e aplica. Edições locais ainda no
  // debounce são descartadas — eram em cima da lista desatualizada.
  const reloadAfterConflict = async () => {
    const conflict = syncConflictRef.current;
    if (!conflict) return;
    let progress = conflict.progress;
    try {
      const fresh = await window.ScolistAPI.loadWizardProgress(conflict.uid, conflict.schoolId);
      if (fresh) progress = fresh;
    } catch (e) {
      console.warn("Falha ao buscar a versão atual da lista, usando a que veio no conflito:", e);
    }
    if (!progress) { window.location.reload(); return; }
    clearTimeout(pendingSyncTimeoutRef.current);
    pendingSyncRef.current = null;
    applyRestoredProgress(progress);
    setSyncConflict(null);
  };

  // Inatividade (pedido de 26/10/2026): aba esquecida aberta por horas
  // gravava a lista velha por cima de quem estava editando (Eleva Recife,
  // 23–26/10). O 409 do autosave já impede a sobrescrita, mas a pessoa
  // perdia a edição feita na aba velha. Agora:
  // - navegador inteiro sem uso por IDLE_LOGOUT_MS -> logout, com aviso no
  //   login (o login do Firebase é um só pras abas, ver ACTIVITY_KEY);
  // - só esta aba parada (outra aba ativa) -> recarrega a lista do servidor.
  // Nos dois casos a interação que "acordou" a aba é engolida (capture,
  // antes do React), pra nenhuma edição sair em cima da lista velha.
  const [loginNotice, setLoginNotice] = useState(null);
  const sessionUid = account && account.user && account.user.id;
  const sessionSchoolId = (account && account.school && account.school.id) || null;
  useIdleSession(sessionUid ? `${sessionUid}:${sessionSchoolId || "_none"}` : null, {
    onExpire: async () => {
      await flushWizardSave();
      await handleLogout({ idle: true });
    },
    onStale: async () => {
      if (syncConflictRef.current) { await reloadAfterConflict(); return; }
      try {
        const fresh = await window.ScolistAPI.loadWizardProgress(sessionUid, sessionSchoolId);
        if (fresh) {
          clearTimeout(pendingSyncTimeoutRef.current);
          pendingSyncRef.current = null;
          applyRestoredProgress(fresh);
        }
      } catch (e) {
        console.warn("Falha ao recarregar a lista depois da aba parada:", e);
      }
    },
  });

  const [historyOpen, setHistoryOpen] = useState(false);

  // Flush de emergência do autosave: o setTimeout de 350ms acima é
  // cancelado (clearTimeout) sempre que o efeito reroda ou o componente
  // desmonta — inclusive quando a aba simplesmente fecha, caso em que ele
  // nunca chega a disparar e a ÚLTIMA mudança (normalmente a mais
  // importante: "acabei de montar a lista, vou fechar e abrir no outro
  // computador") nunca sai do localStorage daquele navegador. Reproduz
  // exatamente o sintoma que a Joana reportou de novo depois do fix
  // original de sincronização (29/08 e 31/08/2026): lista feita no
  // Computador A não aparece no B.
  //
  // visibilitychange (aba escondida — trocou de app, minimizou, fechou)
  // cobre muito mais casos reais que beforeunload sozinho, que não
  // dispara de forma confiável em boa parte do mobile; pagehide cobre o
  // fechamento/navegação em si. Os dois chamam saveWizardProgressKeepalive
  // (api.js), que usa fetch keepalive:true — o único jeito de garantir que
  // o request saia antes da página sumir.
  useEffect(() => {
    const flush = () => {
      if (!pendingSyncRef.current || syncConflictRef.current) return;
      const { uid, schoolId, payload } = pendingSyncRef.current;
      clearTimeout(pendingSyncTimeoutRef.current);
      // Com a base atual (sem esperar resposta): se ainda houver PUT da
      // fila em voo, este leva 409 e a edição fica só no localStorage, que
      // resolveWizardProgress sobe no próximo load se ninguém mais salvou.
      window.ScolistAPI.saveWizardProgressKeepalive(uid, schoolId, withWizardBase(`${LS_KEY}:${uid}:${schoolId || "_none"}`, payload));
      pendingSyncRef.current = null;
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") flush();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("pagehide", flush);
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("pagehide", flush);
    };
  }, []);

  const canNext = true;

  const nextLabel = (() => {
    if (current === 0) return "Vamos nessa";
    if (current === STEPS.length - 2) return "Gerar link final";
    if (current === STEPS.length - 1) return "Finalizar";
    return "Continuar";
  })();

  const onNext = () => {
    if (current === STEPS.length - 1) {
      setCurrent(0);
      setData(prev => ({
        schoolName: resolveSchoolName(account),
        runtimeSelection: prev.runtimeSelection || null,
      }));
      return;
    }
    setCurrent(c => Math.min(STEPS.length - 1, c + 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const onPrev = () => { setCurrent(c => Math.max(0, c - 1)); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const onJump = i => { setCurrent(i); window.scrollTo({ top: 0, behavior: "smooth" }); };

  // Encerra a sessão (Firebase + Scolex) e zera o wizard local — evita que o
  // próximo login nesse navegador herde progresso de outra escola/usuário.
  // `{ idle: true }`: sessão encerrada por inatividade — vai direto pro login
  // com o aviso, em vez da Home.
  const handleLogout = async (opts) => {
    const idle = Boolean(opts && opts.idle === true);
    try {
      if (window.ScolistAPI && typeof window.ScolistAPI.logout === "function") {
        await window.ScolistAPI.logout();
      }
    } catch (e) {
      console.warn("Falha ao encerrar sessão:", e);
    }
    // Não apaga mais o progresso salvo (nem o slot genérico nem o
    // escopado por conta) — só limpa o estado em memória, pra tela de
    // login aparecer limpa. Se a mesma conta logar de novo,
    // loadScopedProgress recupera tudo pelo slot escopado por uid (ver
    // comentário em lsKeyFor). Antes o localStorage.removeItem(LS_KEY)
    // destruía tudo que não tinha persistência própria no backend.
    setCurrent(0);
    setData({});
    setAccount(null);
    if (idle) {
      setLoginNotice("Sua sessão foi encerrada depois de 15 minutos sem uso. Entre de novo para continuar — a lista abre na versão mais recente.");
      goToLogin();
    } else {
      goBackToHome();
    }
  };

  // Reabre o seletor de escola a partir de dentro do app (menu lateral,
  // Shell) — usa a lista de escolas já carregada no login
  // (data.runtimeSelection.availableSchools), sem precisar ir no backend
  // de novo. Só existe quando a conta atende mais de uma escola.
  const switchSchool = () => {
    const schools = (data.runtimeSelection && data.runtimeSelection.availableSchools) || [];
    if (schools.length < 2) return;
    setPendingSchoolPick({ schools, selectedSchoolId: (data.runtimeSelection && data.runtimeSelection.schoolId) || null });
  };

  const handleSchoolPick = async (schoolId) => {
    const context = await window.ScolistAPI.selectSchool(schoolId);
    setPendingSchoolPick(null);
    await completeLogin(context);
  };

  // Entrando pela área admin (#admin): o time vê todas as escolas, então o
  // login pedia pra escolher uma antes — que a área admin não usa. Escolhe
  // sozinho a última (ou a primeira) e segue direto pro #admin.
  const isAdminHash = hash === "admin" || hash.startsWith("admin/");
  const autoPickRef = useRef(false);
  useEffect(() => {
    if (!isAdminHash || account || !pendingSchoolPick || autoPickRef.current) return;
    const first = pendingSchoolPick.selectedSchoolId || (pendingSchoolPick.schools[0] && pendingSchoolPick.schools[0].id);
    if (!first) return;
    autoPickRef.current = true;
    handleSchoolPick(first).catch(() => { autoPickRef.current = false; });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdminHash, account, pendingSchoolPick]);

  const steps = [
    <Step0_Welcome/>, <Step1_Calendar/>, <Step2_Themes/>,
    <Step3_Scolex/>, <Step5_Calibration/>,
    <Step9_Recommend/>, <Step10_Detail/>,
    <StepDidaticosConfig/>, <StepDidaticosBuilder/>,
    <StepPapelariaIntro/>, <StepPapelariaBuilder/>,
    <StepComplementos/>,
    <Step11_Confirm/>, <Step8_Pricing/>, <Step13_Link/>,
  ];

  const schoolName = resolveSchoolName(account);
  const canSwitchSchool = ((data.runtimeSelection && data.runtimeSelection.availableSchools) || []).length > 1;
  const ctxValue = {
    data, setData, schoolName, account, setAccount, logout: handleLogout, switchSchool, canSwitchSchool,
    openHistory: () => setHistoryOpen(true), flushWizardSave, applyRestoredProgress,
    // Atalho pra área admin (#admin, pages-familia.jsx) — só o time Scolados.
    openAdmin: account && account.user && account.user.role === "adm-system" ? () => setHash("admin") : null,
  };

  // Link público (/<slug> ou #lista/<schoolId>) não depende de sessão — resolve antes
  // de esperar authResolved, senão um pai sem login fica preso no
  // LoadingScreen genérico até a checagem de auth (que nunca vai logar
  // ninguém) terminar.
  if (publicKey) {
    if (publicListState.status === "ready") {
      const base = `lista/${publicListState.schoolId}`;
      const route = publicSeriesIdx == null ? base : `${base}/serie/${publicSeriesIdx}`;
      const publicNode = window.renderFamiliaRoute
        ? window.renderFamiliaRoute(route, publicListState.data, goPublic, base)
        : null;
      // Sem a conta de quem está vendo: FamilyFrame/HubFamilia priorizam
      // account.school (logo, cor, instruções) pro preview interno, e aqui
      // isso trocava a logo da escola da lista pela da escola logada — saía
      // errada no PDF pra quem abria o link logado (time, outra escola).
      // A escola do link já vem resolvida em data pelo backend.
      if (publicNode) return <AppCtx.Provider value={{ ...ctxValue, account: null }}>{publicNode}</AppCtx.Provider>;
    }
    return window.PublicListStatus ? <window.PublicListStatus status={publicListState.status}/> : null;
  }

  if (!authResolved) {
    return (
      <AppCtx.Provider value={ctxValue}>
        <LangToggle/>
        <LoadingScreen/>
      </AppCtx.Provider>
    );
  }

  // Área admin (#admin) também ocupa a tela inteira; sem conta logada
  // renderAdminRoute devolve null e o fluxo segue pro login.
  const adminNode = window.renderAdminRoute ? window.renderAdminRoute(hash, account) : null;
  if (adminNode) {
    return (
      <AppCtx.Provider value={ctxValue}>
        {adminNode}
        <AuthErrorToast/>
      </AppCtx.Provider>
    );
  }

  // Family route takes over the whole viewport (outside the assistant shell)
  const familiaNode = window.renderFamiliaRoute ? window.renderFamiliaRoute(hash, data, setHash) : null;
  if (familiaNode) {
    return (
      <AppCtx.Provider value={ctxValue}>
        <LangToggle/>
        {familiaNode}
      </AppCtx.Provider>
    );
  }

  if (pendingSchoolPick && isAdminHash && !account) {
    return (
      <AppCtx.Provider value={ctxValue}>
        <LoadingScreen/>
      </AppCtx.Provider>
    );
  }

  if (pendingSchoolPick) {
    return (
      <AppCtx.Provider value={ctxValue}>
        <LangToggle/>
        <SchoolPicker
          schools={pendingSchoolPick.schools}
          currentSchoolId={pendingSchoolPick.selectedSchoolId}
          onPick={handleSchoolPick}
          onCancel={account
            ? () => setPendingSchoolPick(null)
            : async () => { await window.ScolistAPI.logout(); setPendingSchoolPick(null); setAccount(null); }}
          cancelLabel={account ? "Cancelar" : "Sair e usar outra conta"}
        />
      </AppCtx.Provider>
    );
  }

  // #admin sem login vai direto pro login (sem passar pela Home).
  if (!account && !showLogin && !isAdminHash) {
    return (
      <AppCtx.Provider value={ctxValue}>
        <MarketingHome onEnter={goToLogin} />
      </AppCtx.Provider>
    );
  }

  if (!account) {
    return (
      <AppCtx.Provider value={ctxValue}>
        <AccessGate
          onBack={goBackToHome}
          notice={loginNotice}
          onLoggedIn={async (context) => {
            setLoginNotice(null);
            window.ScolistAnalytics.event("login");
            if (context && context.needsSchoolPick) {
              setPendingSchoolPick({ schools: context.schools, selectedSchoolId: context.selectedSchoolId });
              return;
            }
            // Mesma lógica do efeito de mount acima — recupera o progresso
            // dessa conta (local ou do backend, o que estiver mais
            // atualizado) se ela já tinha usado esse navegador antes, ou já
            // tinha sincronizado de outro dispositivo.
            await completeLogin(context);
            setCurrent(0);
          }}
        />
      </AppCtx.Provider>
    );
  }

  return (
    <AppCtx.Provider value={ctxValue}>
      <LangToggle/>
      <Shell
        current={current} onJump={onJump}
        onPrev={onPrev} onNext={onNext}
        canNext={canNext} nextLabel={nextLabel}>
        <div key={current} style={{ animation: "fadeIn 0.4s ease both" }}>
          {steps[current]}
        </div>
        <style>{`
          @keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
          ::-webkit-scrollbar { width: 10px; height: 10px; }
          ::-webkit-scrollbar-thumb { background: #cfe5e7; border-radius: 5px; }
          ::-webkit-scrollbar-thumb:hover { background: var(--teal); }
          input[type="range"] { height: 4px; }
        `}</style>
      </Shell>
      {historyOpen && window.HistoryPanel && <window.HistoryPanel onClose={() => setHistoryOpen(false)}/>}
      {syncConflict && <SyncConflictBanner onReload={reloadAfterConflict}/>}
      <AuthErrorToast/>
    </AppCtx.Provider>
  );
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(<App />);
